/**
 * Stripe Client Utility
 * Handles Stripe Checkout and Customer Portal redirection
 */

/**
 * Create a Stripe Checkout session and redirect the user
 * @param priceId - The Stripe price ID to subscribe to
 * @param accessToken - Supabase auth token
 * @returns Promise that resolves when redirect happens or rejects on error
 */
export async function redirectToCheckout(
  priceId: string,
  accessToken: string
): Promise<void> {
  try {
    // Call our API to create a checkout session
    const baseUrl = import.meta.env.VITE_API_BASE_URL || window.location.origin
    const response = await fetch(`${baseUrl}/api/create-checkout-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ priceId }),
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Failed to create checkout session')
    }

    // Redirect to Stripe Checkout URL directly
    // Modern Stripe.js recommends using the URL directly instead of redirectToCheckout
    if (data.url) {
      window.location.href = data.url
    } else {
      throw new Error('No checkout URL returned from server')
    }
  } catch (error) {
    console.error('Error redirecting to checkout:', error)
    throw error
  }
}

/**
 * Create a guest checkout session (no authentication required)
 * @param priceId - The Stripe price ID to subscribe to
 * @returns Promise that resolves when redirect happens or rejects on error
 */
export async function redirectToGuestCheckout(priceId: string): Promise<void> {
  try {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || window.location.origin
    const response = await fetch(`${baseUrl}/api/create-guest-checkout-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ priceId }),
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Failed to create checkout session')
    }

    // Redirect to Stripe Checkout URL
    if (data.url) {
      window.location.href = data.url
    } else {
      throw new Error('No checkout URL returned from server')
    }
  } catch (error) {
    console.error('Error redirecting to checkout:', error)
    throw error
  }
}

/**
 * Create a Customer Portal session and redirect the user
 * @param accessToken - Supabase auth token
 * @returns Promise that resolves when redirect happens or rejects on error
 */
export async function redirectToCustomerPortal(accessToken: string): Promise<void> {
  try {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || window.location.origin
    const response = await fetch(`${baseUrl}/api/create-portal-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Failed to create portal session')
    }

    // Redirect to Customer Portal
    window.location.href = data.url
  } catch (error) {
    console.error('Error redirecting to customer portal:', error)
    throw error
  }
}

/**
 * Check user's subscription status
 * @param accessToken - Supabase auth token
 * @returns Subscription status and details
 */
export async function checkSubscription(accessToken: string): Promise<{
  hasSubscription: boolean
  subscription?: {
    id: string
    status: string
    isTrialing: boolean
    trialEnd: string | null
    currentPeriodEnd: string
    cancelAtPeriodEnd: boolean
    priceId: string
  }
  message?: string
}> {
  try {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || window.location.origin
    const response = await fetch(`${baseUrl}/api/check-subscription`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Failed to check subscription')
    }

    return data
  } catch (error) {
    console.error('Error checking subscription:', error)
    throw error
  }
}
